import { MessagesSquareIcon } from 'lucide-react';
import { createElement } from 'react';

import { defineModule } from '@/shared/registry/types';

import { useUnread } from './api';
import { ChatSearchResult } from './components/ChatSearchResult';
import { MessageSearchResult } from './components/MessageSearchResult';
import { MessengerTopBarButton } from './components/MessengerTopBarButton';
import { GroupChatTabEntry } from './entries';
import { setupMessengerRealtime } from './realtime';

/** Sidebar counter: unread messages (muted chats excluded), live through realtime. */
const useMessengerCounter = (): number | undefined => useUnread().data?.messages;

/**
 * `messenger` module: direct and group chats, realtime messages, read receipts, reactions, calls
 * (docs/modules/messenger.md).
 */
export const module = defineModule({
  id: 'messenger',
  routes: [
    {
      path: 'messenger/:chatId?',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages/MessengerPage')).default }),
    },
  ],
  nav: [
    {
      id: 'messenger',
      labelKey: 'messenger:nav.title',
      icon: MessagesSquareIcon,
      to: '/messenger',
      order: 20,
      section: 'main',
      useCounter: useMessengerCounter,
    },
  ],
  topBarWidgets: [{ id: 'messenger', order: 20, component: MessengerTopBarButton }],
  // The chat of a workgroup: its people follow the group (docs/modules/messenger.md "Chats of workgroups").
  groupTabs: [
    {
      id: 'chat',
      labelKey: 'messenger:group.tab',
      order: 15,
      element: (groupId) => createElement(GroupChatTabEntry, { groupId }),
    },
  ],
  // The chats are the first section of the messenger's left pane; other modules add theirs (open lines).
  messengerTabs: [
    {
      id: 'chats',
      labelKey: 'messenger:tabs.chats',
      icon: MessagesSquareIcon,
      to: '/messenger',
      order: 0,
      useCounter: useMessengerCounter,
    },
  ],
  searchRenderers: { chat: ChatSearchResult, message: MessageSearchResult },
  setup: ({ queryClient, realtime }) => setupMessengerRealtime(queryClient, realtime),
});
